import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { useToast } from '../contexts/ToastContext';
import { track } from '../utils/analytics';
import { errMsg } from '../redux/utils/errMsg';
import { getImportExclusions, setImportExclusions, hasImportSelection } from '../utils/djImportSelection';
import './SendToAppModal.scss';

const LABELS = { rekordbox: 'Rekordbox', serato: 'Serato', traktor: 'Traktor', virtualdj: 'Virtual DJ' };

/**
 * "Sync from <DJ app>" picker / manager — the import twin of SendToAppModal
 * (whose styles it shares). This surface manages the SYNC relationship only:
 * checked = synced, unchecked = not synced. It never deletes anything —
 * removing a playlist from MixGoat lives with the other library operations
 * in the Playlists panel.
 *
 * Every checkbox state on an already-synced row is labeled so an uncheck is
 * never ambiguous: "in MixGoat" (synced), "will stop syncing" (unchecked
 * this session), "not synced" (was already excluded when the modal opened).
 *
 * Unchecked keys persist as per-user exclusions (djImportSelection), which
 * one-click re-syncs respect. `onImport(playlistKeys)` runs the import;
 * null = everything selected = the legacy full import (loose tracks too).
 */
export default function SyncFromAppModal({ app, show, onClose, sourcePath, onImport, userId = null }) {
    const toast = useToast();
    const label = LABELS[app] || app;
    const [rows, setRows] = useState(null);
    const [checked, setChecked] = useState(new Set());
    const [initiallyChecked, setInitiallyChecked] = useState(new Set());
    const [busy, setBusy] = useState(false);
    const [query, setQuery] = useState('');
    // Sync-state chips ('all' | 'synced' | 'new') — session-local like the
    // text filter; a sticky filter would read as playlists disappearing.
    const [syncFilter, setSyncFilter] = useState('all');
    const [loadError, setLoadError] = useState(null);
    // Live { phase, current, total } from the main-process import — drives the
    // determinate footer bar so a 20k-track import never reads as stuck.
    const [progress, setProgress] = useState(null);
    const [stopping, setStopping] = useState(false);
    // Library-wide { total, imported } from previewImport — how much of the
    // DJ collection is already in MixGoat. Stays meaningful between runs:
    // reopen after a completed or stopped import and it reflects the DB.
    const [coverage, setCoverage] = useState(null);

    // runDone = tracks created so far in THIS run (high-water mark) — later
    // phases (playlists/cloud) emit no newDone, and without holding the mark
    // the live coverage bar would snap back to its baseline mid-run.
    const [runDone, setRunDone] = useState(0);

    useEffect(() => {
        if (!show) return;
        const off = window.electron.djapp.onImportProgress?.((p) => {
            if (p?.app !== app) return;
            setProgress(p.phase === 'done' ? null : p);
            if (typeof p.newDone === 'number') setRunDone((d) => Math.max(d, p.newDone));
        });
        return () => { off?.(); };
    }, [show, app]);

    useEffect(() => {
        if (!show) return;
        setRows(null); setQuery(''); setSyncFilter('all'); setLoadError(null); setCoverage(null);
        let cancelled = false;
        window.electron.djapp.previewImport(app, sourcePath || undefined).then((res) => {
            if (cancelled) return;
            // { rows, coverage } from current backends; a bare array from an
            // older main process (renderer hot-reloads independently).
            const list = Array.isArray(res) ? res : (res?.rows || []);
            const cov = !Array.isArray(res) && res?.coverage?.total ? res.coverage : null;
            setCoverage(cov);
            // "Tracks not in any playlist" pins FIRST, then every playlist
            // A→Z in ONE run — not new-first. Grouping by sync state threw
            // freshly-imported playlists below hundreds of new crates ("where
            // did the two I just imported go?"); position should mean the
            // name, and the row badges + sync-state chips carry the status.
            const playlistsOnly = (list || []).filter(r => !r.isLoose);
            const looseRow = (list || []).find(r => r.isLoose);
            const sorted = [...playlistsOnly].sort((a, b) => a.name.localeCompare(b.name));
            if (looseRow) sorted.unshift(looseRow);
            setRows(sorted);
            const excluded = new Set(getImportExclusions(app, userId));
            // Manager mode: NEW playlists start unchecked — importing is an
            // explicit opt-in, so a change-notification with hundreds of
            // never-imported crates can't flood in on one Apply. First run
            // (nothing synced yet) keeps everything checked: the user is
            // choosing their initial import and select-all is the natural start.
            const hasSynced = sorted.some(r => !r.isNew && !r.isLoose);
            const startChecked = new Set(sorted
                .filter(r => !excluded.has(r.key) && !(hasSynced && r.isNew && !r.isLoose))
                .map(r => r.key));
            setChecked(startChecked);
            setInitiallyChecked(new Set(startChecked));
            track('dj_import_picker_opened', { app, playlist_count: playlistsOnly.length, new_count: playlistsOnly.filter(r => r.isNew).length, loose_count: looseRow?.trackCount || 0, imported_tracks: cov?.imported, total_tracks: cov?.total });
        }).catch((err) => {
            if (cancelled) return;
            setRows([]);
            setLoadError(errMsg(err));
        });
        return () => { cancelled = true; };
    }, [show, app, sourcePath, userId]);

    // Manager posture: anything already in MixGoat means there are states to
    // manage; before that, keep the first-run checklist free of lifecycle UI.
    const managerMode = (rows || []).some(r => !r.isNew && !r.isLoose);

    const visible = useMemo(() => {
        const q = query.trim().toLowerCase();
        return (rows || []).filter(r => {
            if (q && !r.name.toLowerCase().includes(q)) return false;
            // The loose row belongs to the sync set iff it wasn't excluded
            // when the picker opened — a user who unchecked it must not find
            // it under "Synced" (nor a syncing one under "Not synced").
            if (r.isLoose) {
                if (syncFilter === 'all') return true;
                return (syncFilter === 'synced') === initiallyChecked.has(r.key);
            }
            if (syncFilter === 'synced') return !r.isNew;
            if (syncFilter === 'new') return r.isNew;
            return true;
        });
    }, [rows, query, syncFilter, initiallyChecked]);

    const toggle = (key) => setChecked(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
    const setAll = (on) => setChecked(prev => {
        const n = new Set(prev);
        for (const r of visible) on ? n.add(r.key) : n.delete(r.key);
        return n;
    });

    const selected = (rows || []).filter(r => checked.has(r.key));
    const totalTracks = selected.reduce((t, r) => t + r.trackCount, 0);
    const importNew = selected.filter(r => r.isNew && !r.isLoose).length;
    const keepSynced = selected.filter(r => !r.isNew && !r.isLoose).length;
    // Only rows the user unchecked THIS session count as "stop syncing" —
    // long-excluded rows are standing state, not a new consequence.
    const stopSyncing = (rows || []).filter(r => !checked.has(r.key) && initiallyChecked.has(r.key) && !r.isNew && !r.isLoose).length;
    const looseRow = (rows || []).find(r => r.isLoose);
    // A loose row only has a sync relationship to stop once some of its
    // tracks were imported FROM THIS APP. `newTracks < trackCount` alone
    // can't tell that: it counts library membership from ANY source, so a
    // Rekordbox import sharing files with Serato's loose tracks made a
    // first-ever Serato import claim "Stopped syncing loose tracks" the user
    // never started. Require prior history with this app too: an earlier
    // picker apply (which stores its selection) or imported playlists
    // (legacy full imports brought loose tracks along). On a true first run,
    // unchecking loose is just not opting in, not a consequence.
    const appSyncedBefore = managerMode || hasImportSelection(app, userId);
    const looseStopped = !!(looseRow && appSyncedBefore && looseRow.newTracks < looseRow.trackCount
        && !checked.has(looseRow.key) && initiallyChecked.has(looseRow.key));
    const looseResumedNow = !!(looseRow && looseRow.newTracks < looseRow.trackCount
        && checked.has(looseRow.key) && !initiallyChecked.has(looseRow.key));

    // Sync-state changes made this session — the import summary toast can't
    // see these (it only reports imported content), so without them "no new
    // changes" after unchecking a playlist reads like the action was lost.
    const resumed = selected.filter(r => !r.isNew && !r.isLoose && !initiallyChecked.has(r.key)).length;

    const apply = async () => {
        if (busy || !selected.length) return;
        setBusy(true);
        setProgress(null);
        setStopping(false);
        setRunDone(0);
        try {
            setImportExclusions(app, (rows || []).filter(r => !checked.has(r.key)).map(r => r.key), userId);
            const stateParts = [
                stopSyncing ? `Stopped syncing ${stopSyncing} playlist${stopSyncing === 1 ? '' : 's'}` : null,
                resumed ? `Resumed syncing ${resumed} playlist${resumed === 1 ? '' : 's'}` : null,
                looseStopped ? 'Stopped syncing loose tracks' : null,
                looseResumedNow ? 'Resumed syncing loose tracks' : null,
            ].filter(Boolean);
            const allSelected = selected.length === (rows || []).length;
            await onImport(allSelected ? null : selected.map(r => r.key));
            if (stateParts.length) toast.success(stateParts.join(' · '), 6000);
            onClose();
        } catch (err) {
            toast.error(`${label} sync failed: ${errMsg(err)}`, 9000);
        } finally {
            setBusy(false);
            setProgress(null);
            setStopping(false);
        }
    };

    // Stop is cooperative: the main process aborts between tracks, then the
    // import promise resolves with { canceled: true } and the modal closes.
    const requestStop = () => {
        setStopping(true);
        window.electron.djapp.cancelImport?.(app);
    };

    // The tracks phase is the long pole and gets a real bar; the other phases
    // are short and get an honest label instead of a fake sweep.
    const PHASE_LABELS = {
        reading: `Reading your ${label} library…`,
        playlists: 'Creating playlists…',
        cloud: 'Syncing to your cloud library…',
    };
    // Track-phase progress is framed around NEW tracks when the backend says
    // some are already in the library — with 3k of 4.5k imported, a re-run
    // must not present itself as starting from zero. When the coverage bar is
    // up top it's the ONE moving bar (it fills live from the same events), so
    // the footer keeps only the run's text and drops the duplicate "already
    // in" clause the bar states visually.
    let trackPct = null;
    let trackLabel = null;
    if (progress?.phase === 'tracks' && progress.total) {
        const hasNewSplit = progress.newTotal != null && progress.newTotal < progress.total;
        if (hasNewSplit && progress.newTotal > 0) {
            trackPct = Math.min(100, Math.round(((progress.newDone || 0) / progress.newTotal) * 100));
            trackLabel = `${(progress.newDone || 0).toLocaleString()} / ${progress.newTotal.toLocaleString()} new tracks`
                + (coverage ? '' : ` · ${(progress.total - progress.newTotal).toLocaleString()} already in MixGoat`);
        } else if (hasNewSplit) {
            trackPct = Math.round((progress.current / progress.total) * 100);
            trackLabel = `Checking ${progress.current.toLocaleString()} / ${progress.total.toLocaleString()} · all already in MixGoat`;
        } else {
            trackPct = Math.round((progress.current / progress.total) * 100);
            trackLabel = `${progress.current.toLocaleString()} / ${progress.total.toLocaleString()} tracks`;
        }
    }

    // The coverage bar fills LIVE during a run: baseline coverage (measured
    // when the picker opened) plus tracks created so far. Watching the
    // library fill toward complete IS the progress — one bar, one truth.
    // One solid fill that grows during the run. (A two-tone baseline/run
    // split was tried and cut: dimming the baseline read as "disabled", and
    // two cyans at 6px don't separate — the climbing number and the footer's
    // run bar already carry the this-run story.)
    const liveImported = coverage
        ? Math.min(coverage.total, coverage.imported + (busy ? runDone : 0))
        : null;

    const footerSummary = managerMode ? (
        [
            importNew ? `import ${importNew} new` : null,
            keepSynced ? `keep ${keepSynced} synced` : null,
            stopSyncing ? `stop syncing ${stopSyncing}` : null,
            looseStopped ? 'stop syncing loose tracks' : null,
        ].filter(Boolean).join(' · ') || 'nothing selected'
    ) : (
        `${selected.filter(r => !r.isLoose).length} playlist${selected.filter(r => !r.isLoose).length === 1 ? '' : 's'}${selected.some(r => r.isLoose) ? ' + loose tracks' : ''} · ${totalTracks} track${totalTracks === 1 ? '' : 's'}`
    );

    return (
        <Modal show={show} onHide={busy ? undefined : onClose} centered className="send-to-app-modal sync-from-app-modal" size="lg">
            <Modal.Header closeButton={!busy}>
                <Modal.Title>{managerMode ? `Manage ${label} imports` : `Import from ${label}`}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="send-hint">
                    Choose what to bring into MixGoat — playlists, and tracks outside any playlist. Your {label} library is never modified.
                    Unchecked items stay out on future re-syncs (their MixGoat copies are kept — delete those from the Playlists panel).
                </p>
                <div className="send-toolbar">
                    <input className="send-search" placeholder="Filter playlists…" value={query} onChange={(e) => setQuery(e.target.value)} />
                    <button type="button" className="send-link" onClick={() => setAll(true)}>Select all</button>
                    <button type="button" className="send-link" onClick={() => setAll(false)}>None</button>
                </div>
                {/* Sync-state chips — manager mode only: on a first run every
                    row is "not synced" and the split would be noise. Toggles,
                    no "All" chip — same convention as the sidebar's source
                    chips: click to filter, click the active one to clear.
                    Select all / None act on the filtered view, so chip +
                    Select all composes into "select every synced playlist". */}
                {managerMode && (
                    <div className="send-sync-chips" role="group" aria-label="Filter playlists by sync state">
                        {[
                            { key: 'synced', label: 'Synced', count: (rows || []).filter(r => !r.isLoose && !r.isNew).length },
                            { key: 'new', label: 'Not synced', count: (rows || []).filter(r => !r.isLoose && r.isNew).length },
                        ].map((chip) => {
                            const isOn = syncFilter === chip.key;
                            return (
                                <button
                                    key={chip.key}
                                    type="button"
                                    className={`sync-chip${isOn ? ' active' : ''}`}
                                    aria-pressed={isOn}
                                    title={isOn ? `Clear ${chip.label} filter` : undefined}
                                    onClick={() => {
                                        const next = isOn ? 'all' : chip.key;
                                        setSyncFilter(next);
                                        if (next !== 'all') track('dj_import_picker_filtered', { app, filter: next });
                                    }}
                                >
                                    {chip.label}
                                    <span className="chip-count">{chip.count}</span>
                                </button>
                            );
                        })}
                    </div>
                )}
                {/* Always mounted: coverage resolves after the modal opens,
                    and inserting these two rows above the list later shifted
                    everything below (open-small-then-jump). */}
                <div className="send-coverage">
                    <div className="send-coverage-bar">
                        {coverage && <div className="send-coverage-fill" style={{ width: `${Math.round((liveImported / coverage.total) * 100)}%` }} />}
                    </div>
                    <span className="send-coverage-label">
                        {coverage
                            ? <>{liveImported.toLocaleString()} of {coverage.total.toLocaleString()} {label} tracks in MixGoat{busy && progress ? ' · importing…' : ''}</>
                            : '\u00A0'}
                    </span>
                </div>
                {rows === null ? (
                    <ul className="send-list send-skel" aria-label={`Reading your ${label} library`}>
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                            <li key={i} className="send-row send-skel-row">
                                <span className="send-skel-box" />
                                <div className="send-row-text">
                                    <span className="send-skel-line" />
                                    <span className="send-skel-line short" />
                                </div>
                            </li>
                        ))}
                    </ul>
                ) : visible.length === 0 ? (
                    <div className="send-empty">{loadError ? `Couldn't read ${label}: ${loadError}` : `No playlists${query || syncFilter !== 'all' ? ' match' : ''}.`}</div>
                ) : (
                    <ul className="send-list">
                        {visible.map((r) => {
                            const isOn = checked.has(r.key);
                            const isSynced = !r.isNew && !r.isLoose;
                            const looseInMixGoat = r.isLoose && r.newTracks < r.trackCount;
                            return (
                                <li key={r.key} className={`send-row${isOn ? ' checked' : ''}${r.isLoose ? ' loose-row' : ''}`} onClick={() => toggle(r.key)}>
                                    <input type="checkbox" checked={isOn} onChange={() => toggle(r.key)} onClick={(e) => e.stopPropagation()} />
                                    <div className="send-row-text">
                                        <div className="send-row-name">
                                            {r.name}
                                            {/* One badge vocabulary: sync state only. The loose
                                                row's destination ("library, no playlist") is
                                                explained by its meta text, not the badge —
                                                "library only" here read as a third state. A
                                                loose row counts as in MixGoat once any of its
                                                tracks were imported (newTracks < trackCount),
                                                mirroring how playlist rows badge on a match. */}
                                            {r.isNew && !r.isLoose && <span className="send-badge">new</span>}
                                            {(isSynced || looseInMixGoat) && isOn && <span className="send-badge">in MixGoat</span>}
                                            {(isSynced || looseInMixGoat) && !isOn && initiallyChecked.has(r.key) && <span className="send-badge unsync">will stop syncing</span>}
                                            {(isSynced || looseInMixGoat) && !isOn && !initiallyChecked.has(r.key) && <span className="send-badge">not synced</span>}
                                        </div>
                                        <div className="send-row-meta">
                                            {r.isLoose ? (
                                                <>{r.trackCount} track{r.trackCount === 1 ? '' : 's'} in your {label} collection outside every playlist{r.newTracks > 0 ? ` · ${r.newTracks} new` : ''} — imported into your Library, no playlist created</>
                                            ) : (
                                                <>
                                                    {r.trackCount} track{r.trackCount === 1 ? '' : 's'}
                                                    {!r.isNew && r.newTracks > 0 ? ` · ${r.newTracks} new` : ''}
                                                    {r.path && r.path !== r.name ? ` · ${r.path}` : ''}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </Modal.Body>
            <Modal.Footer>
                <div className={`send-footer-summary${busy && progress ? ' has-progress' : ''}`}>
                    {busy && progress ? (
                        <div className="send-import-progress">
                            {trackPct !== null ? (
                                <>
                                    {/* Two bars, two NAMED scopes, both alive: this one
                                        sweeps 0→100% for the run (the rate signal), the
                                        library bar above creeps as its two-tone segment
                                        grows. A single merged bar barely moved on
                                        mostly-imported libraries and read as stuck. */}
                                    <div className="send-import-progress-bar">
                                        <div className="send-import-progress-fill" style={{ width: `${trackPct}%` }} />
                                    </div>
                                    <span className="send-import-progress-label">
                                        This import · {trackPct}% · {trackLabel}
                                    </span>
                                </>
                            ) : (
                                <span className="send-import-progress-label">{PHASE_LABELS[progress.phase] || 'Importing…'}</span>
                            )}
                        </div>
                    ) : footerSummary}
                </div>
                <Button variant="secondary" onClick={busy ? requestStop : onClose} disabled={busy && stopping}>
                    {busy ? (stopping ? 'Stopping…' : 'Stop') : 'Cancel'}
                </Button>
                <Button variant="primary" onClick={apply} disabled={busy || !selected.length}>
                    {busy
                        ? <><Spinner animation="border" size="sm" /> {managerMode ? 'Applying…' : 'Importing…'}</>
                        : managerMode ? (importNew ? `Apply · ${importNew} new` : 'Apply') : `Import from ${label}`}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
